import { getSubjectLabels } from '@/lib/settings';
import React from 'react';
import { Loader2, Save } from 'lucide-react';
import TeacherInvoicesTab from '../../TeacherInvoicesTab';
import TeacherAvailabilityRulesSection from '../TeacherAvailabilityRulesSection';
import PersonCollapsibleSection from '../PersonCollapsibleSection';
import TeacherOrdersSection from '../TeacherOrdersSection';
import TeacherDocumentsSection from '../TeacherDocumentsSection';
import { Button } from '../../../ui/button';
import { Switch } from '../../../ui/switch';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { TabsContent } from '../../../ui/tabs';
import { ImageIcon } from 'lucide-react';
import { MediaPicker } from '../../../cms/MediaPicker';

type TeacherForm = {
  shortCode: string;
  taxNumber: string;
  bankAccountHolder: string;
  bankIban: string;
  subjectIds: number[];
  portraitUrl: string;
  portraitAlt: string;
  publicBio: string;
};

type Props = {
  visible: boolean;
  teacherEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveTeacher: () => void;
  teacherForm: TeacherForm;
  setTeacherForm: React.Dispatch<React.SetStateAction<TeacherForm>>;
  subjectOptions: Array<{ id: number; name: string }>;
  teachingArts: Array<{ id: number; name: string }>;
  teacherArtRates: Record<number, string>;
  setTeacherArtRates: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  personStandorteIds: number[];
  standorte: Array<{ id: number; name: string }>;
  availabilityStandortId: number | null;
  setAvailabilityStandortId: (id: number | null) => void;
  availabilityEditing: boolean;
  setAvailabilityEditing: (editing: boolean) => void;
  availabilityLoading: boolean;
  onReloadAvailability: () => void;
  onSaveAvailability: () => void;
  availabilitySlots: Array<{ dayOfWeek: number; time_start: string; time_end: string }>;
  daysOfWeek: Array<{ id: number; label: string }>;
  addSlotForDay: (dayOfWeek: number) => void;
  setSlotTime: (idx: number, field: 'time_start' | 'time_end', value: string) => void;
  removeSlot: (idx: number) => void;
  wpUserId: number;
  /** Lehrer-Rolle aktiv (T-0043); fehlt der Handler, gibt es den Schalter nicht. */
  roleActive?: boolean;
  onToggleRoleActive?: (aktiv: boolean) => void;
};

export default function PersonTeacherTab({
  visible,
  teacherEditing,
  onStartEdit,
  onCancelEdit,
  onSaveTeacher,
  teacherForm,
  setTeacherForm,
  subjectOptions,
  teachingArts,
  teacherArtRates,
  setTeacherArtRates,
  personStandorteIds,
  standorte,
  availabilityStandortId,
  setAvailabilityStandortId,
  availabilityEditing,
  setAvailabilityEditing,
  availabilityLoading,
  onReloadAvailability,
  onSaveAvailability,
  availabilitySlots,
  daysOfWeek,
  addSlotForDay,
  setSlotTime,
  removeSlot,
  wpUserId,
  roleActive = true,
  onToggleRoleActive,
}: Readonly<Props>) {
  const [portraitPickerOpen, setPortraitPickerOpen] = React.useState(false);

  if (!visible) return null;

  const toggleSubject = (subjectId: number, checked: boolean) => {
    setTeacherForm((prev) => {
      if (checked) {
        return { ...prev, subjectIds: [...prev.subjectIds, subjectId] };
      }
      return { ...prev, subjectIds: prev.subjectIds.filter((id) => id !== subjectId) };
    });
  };

  const teacherEditActions = teacherEditing ? (
    <>
      <Button variant="outline" onClick={onCancelEdit}>Abbrechen</Button>
      <Button onClick={onSaveTeacher}>Speichern</Button>
    </>
  ) : (
    <Button variant="outline" onClick={onStartEdit}>Bearbeiten</Button>
  );

  const availabilityActions = availabilityEditing ? (
    <>
      <Button variant="outline" onClick={onReloadAvailability} disabled={availabilityLoading}>
        Abbrechen
      </Button>
      <Button onClick={onSaveAvailability} disabled={availabilityLoading}>
        {availabilityLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
        Speichern
      </Button>
    </>
  ) : (
    <Button variant="outline" onClick={() => setAvailabilityEditing(true)} disabled={availabilityLoading || !availabilityStandortId}>
      Bearbeiten
    </Button>
  );

  return (
    <TabsContent value="teacher" className="space-y-4">
      {onToggleRoleActive ? (
        <div className="flex items-center justify-between rounded-lg border px-4 py-3">
          <div>
            <div className="font-medium">Lehrer-Rolle aktiv</div>
            <div className="text-sm text-muted-foreground">
              Inaktive Lehrkräfte erscheinen in der Personenverwaltung nur mit „Alle Status“.
            </div>
          </div>
          <Switch checked={roleActive} onCheckedChange={(v) => onToggleRoleActive(v === true)} />
        </div>
      ) : null}
      <PersonCollapsibleSection
        title="Stammdaten"
        description="Kürzel, Steuerdaten und Bankverbindung"
        actions={teacherEditActions}
        defaultOpen
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="teacherShort" className="text-sm font-medium">Kürzel</label>
            {teacherEditing ? (
              <Input id="teacherShort" value={teacherForm.shortCode} onChange={e => setTeacherForm(prev => ({ ...prev, shortCode: e.target.value }))} />
            ) : (
              <div className="rounded-md border p-3">{teacherForm.shortCode || '-'}</div>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="teacherTax" className="text-sm font-medium">Steuernummer</label>
            {teacherEditing ? (
              <Input id="teacherTax" value={teacherForm.taxNumber} onChange={e => setTeacherForm(prev => ({ ...prev, taxNumber: e.target.value }))} />
            ) : (
              <div className="rounded-md border p-3">{teacherForm.taxNumber || '-'}</div>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="teacherAccountHolder" className="text-sm font-medium">Kontoinhaber</label>
            {teacherEditing ? (
              <Input id="teacherAccountHolder" value={teacherForm.bankAccountHolder} onChange={e => setTeacherForm(prev => ({ ...prev, bankAccountHolder: e.target.value }))} />
            ) : (
              <div className="rounded-md border p-3">{teacherForm.bankAccountHolder || '-'}</div>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="teacherIban" className="text-sm font-medium">IBAN</label>
            {teacherEditing ? (
              <Input id="teacherIban" value={teacherForm.bankIban} onChange={e => setTeacherForm(prev => ({ ...prev, bankIban: e.target.value }))} />
            ) : (
              <div className="rounded-md border p-3">{teacherForm.bankIban || '-'}</div>
            )}
          </div>
        </div>
      </PersonCollapsibleSection>

      <PersonCollapsibleSection
        title="Website-Profil (Coach)"
        description={`Portrait und öffentliche Beschreibung für die Standort × ${getSubjectLabels().singular}-Seiten`}
        actions={teacherEditActions}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="text-sm font-medium">Portrait</div>
            <div className="flex items-start gap-3">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted/30">
                {teacherForm.portraitUrl ? (
                  <img src={teacherForm.portraitUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              {teacherEditing ? (
                <div className="flex flex-col gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setPortraitPickerOpen(true)}>
                    Aus Bibliothek
                  </Button>
                  {teacherForm.portraitUrl ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setTeacherForm((prev) => ({ ...prev, portraitUrl: '' }))}
                    >
                      Entfernen
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="space-y-2">
            <label htmlFor="teacherPortraitAlt" className="text-sm font-medium">Portrait Alt-Text</label>
            {teacherEditing ? (
              <Input
                id="teacherPortraitAlt"
                value={teacherForm.portraitAlt}
                onChange={(e) => setTeacherForm((prev) => ({ ...prev, portraitAlt: e.target.value }))}
              />
            ) : (
              <div className="rounded-md border p-3">{teacherForm.portraitAlt || '-'}</div>
            )}
          </div>
          <div className="space-y-2 md:col-span-2">
            <label htmlFor="teacherBio" className="text-sm font-medium">Öffentliche Beschreibung</label>
            {teacherEditing ? (
              <Textarea
                id="teacherBio"
                rows={4}
                value={teacherForm.publicBio}
                onChange={(e) => setTeacherForm((prev) => ({ ...prev, publicBio: e.target.value }))}
                placeholder={`Kurzer Text, der auf den Standort × ${getSubjectLabels().singular}-Seiten unter dem Namen erscheint.`}
              />
            ) : (
              <div className="whitespace-pre-wrap rounded-md border p-3 text-sm">{teacherForm.publicBio || '-'}</div>
            )}
          </div>
        </div>
        <MediaPicker
          open={portraitPickerOpen}
          onClose={() => setPortraitPickerOpen(false)}
          onSelect={(url) => setTeacherForm((prev) => ({ ...prev, portraitUrl: url }))}
        />
      </PersonCollapsibleSection>

      <PersonCollapsibleSection
        title="Dokumente"
        description="Verträge und hochgeladene Unterlagen"
      >
        <TeacherDocumentsSection wpUserId={wpUserId} />
      </PersonCollapsibleSection>

      <PersonCollapsibleSection
        title="Unterricht & Honorare"
        description="Fächerzuordnung und Honorarsätze je Unterrichtsform"
        actions={teacherEditActions}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="text-sm font-medium">{getSubjectLabels().plural} (mindestens 1)</div>
            {teacherEditing ? (
              <div className="max-h-56 overflow-auto rounded-md border p-2">
                {subjectOptions.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Keine {getSubjectLabels().plural} geladen</div>
                ) : (
                  subjectOptions.map(opt => (
                    <label key={opt.id} className="flex items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={teacherForm.subjectIds.includes(opt.id)}
                        onChange={(e) => toggleSubject(opt.id, e.target.checked)}
                      />
                      <span className="text-sm">{opt.name}</span>
                    </label>
                  ))
                )}
              </div>
            ) : (
              <div className="rounded-md border p-3 text-sm">
                {teacherForm.subjectIds.length > 0 ? teacherForm.subjectIds.map(id => subjectOptions.find(o => o.id === id)?.name || id).join(', ') : `Keine ${getSubjectLabels().plural} zugeordnet`}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="rounded-lg border">
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Art</th>
                      <th className="px-4 py-3 font-medium">Honorar pro 60 Minuten</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {teachingArts.map(a => (
                      <tr key={a.id}>
                        <td className="px-4 py-3">{a.name}</td>
                        <td className="px-4 py-3">
                          {teacherEditing ? (
                            <div className="flex items-center">
                              <Input
                                className="max-w-[140px]"
                                type="number"
                                step="0.01"
                                value={teacherArtRates[a.id] ?? ''}
                                onChange={e => setTeacherArtRates(prev => ({ ...prev, [a.id]: e.target.value }))}
                                placeholder="0.00"
                              />
                            </div>
                          ) : (
                            <span>{teacherArtRates[a.id] ? `${teacherArtRates[a.id]} €` : '-'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {teachingArts.length === 0 && (
                      <tr>
                        <td className="px-4 py-3 text-sm text-muted-foreground" colSpan={2}>Keine Unterrichtsform-Arten vorhanden</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Hinweis: Honorar je Art der Unterrichtsform in Euro pro 60 Minuten; für kürzere Termine wird anteilig gerechnet.</p>
          </div>
        </div>
      </PersonCollapsibleSection>

      <PersonCollapsibleSection
        title="Freigabezeiten"
        description="Wöchentlich wiederkehrende Verfügbarkeit pro Standort"
        actions={availabilityActions}
      >
        <div className="space-y-4">
          {personStandorteIds.length === 0 ? (
            <div className="rounded-md border p-4 text-sm text-muted-foreground">
              Bitte zuerst mindestens einen Standort im Tab „Allgemein“ zuordnen, um Freigabezeiten zu pflegen.
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="text-sm font-medium">Standort</div>
                <Select
                  value={availabilityStandortId ? String(availabilityStandortId) : ''}
                  onValueChange={(v) => {
                    setAvailabilityStandortId(v ? Number(v) : null);
                    setAvailabilityEditing(false);
                  }}
                >
                  <SelectTrigger className="min-w-[260px]">
                    <SelectValue placeholder="Standort auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {personStandorteIds.map((sid) => {
                      const s = standorte.find(x => x.id === sid);
                      if (!s) return null;
                      return (
                        <SelectItem key={sid} value={String(sid)}>
                          {s.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {availabilityLoading ? (
                <div className="flex h-32 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  {daysOfWeek.map(d => {
                    const daySlots = availabilitySlots
                      .map((s, idx) => ({ slot: s, idx }))
                      .filter(x => x.slot.dayOfWeek === d.id)
                      .sort((a, b) => String(a.slot.time_start).localeCompare(String(b.slot.time_start)));

                    return (
                      <div key={d.id} className="rounded-lg border p-3">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{d.label}</div>
                          {availabilityEditing && (
                            <Button variant="outline" size="sm" onClick={() => addSlotForDay(d.id)}>
                              Slot hinzufügen
                            </Button>
                          )}
                        </div>
                        {daySlots.length === 0 ? (
                          <div className="mt-2 text-sm text-muted-foreground">Keine Freigabezeiten</div>
                        ) : (
                          <div className="mt-2 space-y-2">
                            {daySlots.map(({ slot, idx }) => (
                              <div key={`${d.id}-${idx}`} className="flex flex-col gap-2 md:flex-row md:items-center">
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="time"
                                    className="w-[140px]"
                                    value={(slot.time_start || '').slice(0, 5)}
                                    disabled={!availabilityEditing}
                                    onChange={(e) => setSlotTime(idx, 'time_start', e.target.value)}
                                  />
                                  <span className="text-sm text-muted-foreground">bis</span>
                                  <Input
                                    type="time"
                                    className="w-[140px]"
                                    value={(slot.time_end || '').slice(0, 5)}
                                    disabled={!availabilityEditing}
                                    onChange={(e) => setSlotTime(idx, 'time_end', e.target.value)}
                                  />
                                </div>
                                <div className="flex-1" />
                                {availabilityEditing && (
                                  <Button variant="outline" className="text-destructive" onClick={() => removeSlot(idx)}>
                                    Entfernen
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <TeacherAvailabilityRulesSection
            wpUserId={wpUserId}
            standorte={standorte}
            personStandorteIds={personStandorteIds}
            defaultStandortId={availabilityStandortId}
          />
        </div>
      </PersonCollapsibleSection>

      <PersonCollapsibleSection
        title="Aufträge"
        description="Lehreraufträge und Bestätigungen"
      >
        <TeacherOrdersSection wpUserId={wpUserId} />
      </PersonCollapsibleSection>

      <PersonCollapsibleSection
        title="Rechnungen"
        description="Lehrerrechnungen und Abrechnungen"
      >
        <TeacherInvoicesTab lehrerId={0} wpUserId={wpUserId} />
      </PersonCollapsibleSection>
    </TabsContent>
  );
}
